import type { ReactNode } from 'react';
import type { TicketStatus, TicketPriority, TicketArea } from '../lib/api';
import {
  toneClass,
  statusTone,
  statusLabel,
  priorityTone,
  priorityLabel,
  type Tone,
} from '../lib/ticket-ui';
import { HoverCard } from './ui/HoverCard';

interface BadgeProps {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}

/**
 * The single badge primitive. One shape, one type size (text-xs), tone-driven
 * color from the capped semantic palette in ticket-ui.ts. Replaces the ad-hoc
 * inline `bg-…-100 text-…-800` pills scattered across pages.
 */
export function Badge({ tone = 'neutral', children, className = '' }: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${toneClass[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

export function StatusBadge({ status, className }: { status: TicketStatus; className?: string }) {
  return (
    <Badge tone={statusTone[status]} className={className}>
      {statusLabel[status]}
    </Badge>
  );
}

export function PriorityBadge({ priority, className }: { priority: TicketPriority; className?: string }) {
  return (
    <Badge tone={priorityTone[priority]} className={className}>
      {priorityLabel[priority]}
    </Badge>
  );
}

// Long reasons are cut so the hover card stays a glance, not a page.
const BLOCKED_PREVIEW_MAX = 400;

/**
 * Status badge for list rows. When the ticket is Blocked, hovering (or focusing)
 * the badge shows the blocked reason so nobody has to open the ticket to see it.
 */
export function ListStatusBadge({
  status,
  blockedReason,
}: {
  status: TicketStatus;
  blockedReason: string | null;
}) {
  if (status !== 'BLOCKED') return <StatusBadge status={status} />;
  const reason = blockedReason?.trim();
  const preview =
    reason && reason.length > BLOCKED_PREVIEW_MAX ? `${reason.slice(0, BLOCKED_PREVIEW_MAX)}…` : reason;
  return (
    <HoverCard
      content={
        <>
          <div className="font-semibold text-rose-700 mb-1">Blocked reason</div>
          {preview ? (
            <div className="whitespace-pre-wrap break-words">{preview}</div>
          ) : (
            <div className="italic text-slate-500">No reason given.</div>
          )}
        </>
      }
    >
      <StatusBadge status={status} className="underline decoration-dotted underline-offset-2" />
    </HoverCard>
  );
}

/**
 * Marks a ticket an admin has placed in the priority queue. Ranked tickets sort
 * above all unranked ones (lowest rank first), regardless of their priority, so
 * the badge explains why a ticket sits where it does in the list.
 */
export function RankBadge({ rank, className }: { rank: number; className?: string }) {
  return (
    <span title={`Priority rank ${rank}: placed by an admin, so it sorts above unranked tickets`}>
      <Badge tone="active" className={className}>
        <svg className="w-3 h-3 mr-1" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M10 17a.75.75 0 0 1-.75-.75V5.612L5.29 9.77a.75.75 0 0 1-1.08-1.04l5.25-5.5a.75.75 0 0 1 1.08 0l5.25 5.5a.75.75 0 1 1-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0 1 10 17Z"
            clipRule="evenodd"
          />
        </svg>
        Rank {rank}
      </Badge>
    </span>
  );
}

/** A ticket flagged as customer-service work. */
export function CustomerServiceBadge({ className }: { className?: string }) {
  return (
    <Badge tone="warn" className={className}>
      Customer service
    </Badge>
  );
}

/** A ticket reported from the production floor (area = PRODUCTION). */
export function ProductionBadge({ className }: { className?: string }) {
  return (
    <Badge tone="info" className={className}>
      <svg className="w-3 h-3 mr-1" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M8.34 1.804A1 1 0 0 1 9.32 1h1.36a1 1 0 0 1 .98.804l.295 1.473c.497.144.971.342 1.416.587l1.25-.834a1 1 0 0 1 1.262.125l.962.962a1 1 0 0 1 .125 1.262l-.834 1.25c.245.445.443.919.587 1.416l1.473.294a1 1 0 0 1 .804.98v1.361a1 1 0 0 1-.804.98l-1.473.295a6.95 6.95 0 0 1-.587 1.416l.834 1.25a1 1 0 0 1-.125 1.262l-.962.962a1 1 0 0 1-1.262.125l-1.25-.834a6.953 6.953 0 0 1-1.416.587l-.294 1.473a1 1 0 0 1-.98.804H9.32a1 1 0 0 1-.98-.804l-.295-1.473a6.957 6.957 0 0 1-1.416-.587l-1.25.834a1 1 0 0 1-1.262-.125l-.962-.962a1 1 0 0 1-.125-1.262l.834-1.25a6.957 6.957 0 0 1-.587-1.416l-1.473-.295A1 1 0 0 1 1 10.68V9.32a1 1 0 0 1 .804-.98l1.473-.295c.144-.497.342-.971.587-1.416l-.834-1.25a1 1 0 0 1 .125-1.262l.962-.962A1 1 0 0 1 5.38 2.97l1.25.834a6.957 6.957 0 0 1 1.416-.587l.294-1.473ZM10 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
          clipRule="evenodd"
        />
      </svg>
      Production
    </Badge>
  );
}

/**
 * Renders the source/context tags for a ticket (production floor, customer
 * service). Returns null when a ticket has none, so callers can drop it inline
 * without guarding. Used in the dashboard list and the ticket detail header.
 */
export function TicketTags({
  area,
  isCustomerService,
  className = '',
}: {
  area: TicketArea;
  isCustomerService: boolean;
  className?: string;
}) {
  if (area !== 'PRODUCTION' && !isCustomerService) return null;
  return (
    <span className={`inline-flex flex-wrap items-center gap-1.5 ${className}`}>
      {isCustomerService && <CustomerServiceBadge />}
      {area === 'PRODUCTION' && <ProductionBadge />}
    </span>
  );
}
